import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ACCOUNTING_ROUTES, apiMessage } from '../accountingApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onBuilt: () => void;
}

/**
 * `POST /accounting/checks/:checkId/posting` — the operator escape hatch
 * named in the controller's own summary: a settled check the checkout
 * listener could not queue (the venue installed its connector after the sale,
 * a transient failure, a branch the fan-out silently skipped because no
 * installation covered it yet). A check id is asked for directly, the same
 * choice `Fiscal/BuildDocumentDialog` makes and for the same reason: a picker
 * scoped to "checks with no posting" would hide exactly the back-fill case
 * this exists for, and would need its own new endpoint to answer "which
 * checks" that nothing in ADR-0028 provides.
 *
 * Idempotent by construction, not by a client-side guard: the partial unique
 * index `accounting_postings_one_live_sale_per_check` means a second submit
 * for the same check — a double click, or the same check id typed in twice —
 * cannot create a second SALE posting. `AccountingPostingsService.enqueueForCheck`
 * catches the resulting `P2002` and returns the posting that already exists,
 * so this dialog's own `busy` flag below is a UX nicety (no double request in
 * flight, no confusing double toast), not the thing making it safe.
 */
export default function BuildPostingDialog({ open, onClose, onBuilt }: Props) {
  const { t } = useTranslation();
  const [checkId, setCheckId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const id = checkId.trim();
    if (!id) return;
    setBusy(true);
    try {
      await api.post(ACCOUNTING_ROUTES.buildForCheck(id));
      toast.success(t('accounting.buildForCheck.success'));
      setCheckId('');
      onBuilt();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('accounting.buildForCheck.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('accounting.buildForCheck.title')}
      description={t('accounting.buildForCheck.description')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !checkId.trim()}>
            {t('accounting.buildForCheck.submit')}
          </Button>
        </>
      }
    >
      <Field
        label={t('accounting.buildForCheck.checkId')}
        hint={t('accounting.buildForCheck.checkIdHint')}
        controlId="accounting-build-check-id"
      >
        <Input
          value={checkId}
          onChange={(e) => setCheckId(e.target.value)}
          placeholder={t('accounting.buildForCheck.checkIdPlaceholder')}
          autoFocus
        />
      </Field>
    </Dialog>
  );
}
